import apiClient from './config';
import { getCurrentLanguage, SupportedLanguage } from '../config/languageConfig';
import { handleApiError } from './utils';
import { PromotionCodeResponse } from '../types/promotion';

// 프로모션 API 응답 타입
export interface PromotionResponse {
  isOpen: boolean;
  operationTime: {
    dayOfWeek: string;
    startTime: string;
    endTime: string;
  };
  supportedLanguages: string[];
  isClosingSoon: boolean;
  message: string;
}

export interface PromotionBannerStatusResponse {
  bannerEligible: boolean;
  promotionSource: 'STAY_AUTO' | 'USER_CODE' | 'NONE';
  appliedPromotionId: string | null;
  appliedPolicyId: string | null;
  remainingTimeSeconds: number | null;
  messageKey: string;
}

/**
 * 언어 코드를 API 파라미터로 변환
 * @param language 애플리케이션 언어 코드
 * @returns API에서 사용하는 언어 코드
 */
const mapLanguageToApiParam = (language: SupportedLanguage): string => {
  switch (language) {
    case 'zh-CN':
      return 'zh-cn';
    case 'zh-TW':
      return 'zh-tw';
    case 'ja':
      return 'ja';
    case 'en':
      return 'en';
    case 'ko':
      return 'ko';
    default:
      return 'en';
  }
};

/**
 * 프로모션 상태 조회
 * @param lang 언어 설정 (기본값: 현재 설정된 언어)
 * @returns 프로모션 상태 정보
 */
export const getPromotionStatus = async (lang?: string): Promise<PromotionResponse | null> => {
  const endpoint = '/api/v1/promotion-on';
  const currentLang = lang || getCurrentLanguage();
  const apiLang = mapLanguageToApiParam(currentLang as SupportedLanguage);
  
  try {
    const response = await apiClient.get(`${endpoint}?lang=${apiLang}`);
    return response.data;
  } catch (error) {
    const apiError = handleApiError(error);
    console.error(`Error fetching promotion status: ${apiError.message}`, apiError);
    return null;
  }
};

/**
 * 프로모션 코드 조회
 * @param lang 언어 설정 (기본값: 현재 설정된 언어)
 * @param addressId 주소 ID (선택사항)
 * @param promotionUserId 프로모션 사용자 ID (선택사항)
 * @returns 프로모션 코드 정보
 */
export const getPromotionCode = async (lang?: string, addressId?: string, promotionUserId?: string): Promise<PromotionCodeResponse | null> => {
  const endpoint = '/api/v1/promotions/active';
  const currentLang = lang || getCurrentLanguage();
  const apiLang = mapLanguageToApiParam(currentLang as SupportedLanguage);
  
  // 쿼리 파라미터 구성
  const params = new URLSearchParams();
  params.append('lang', apiLang);
  if (addressId) {
    params.append('addressId', addressId);
  }
  if (promotionUserId) {
    params.append('promotionUserId', promotionUserId);
  }
  
  try {
    const response = await apiClient.get(`${endpoint}?${params.toString()}`);
    return response.data;
  } catch (error) {
    const apiError = handleApiError(error);
    console.error(`Error fetching promotion code: ${apiError.message}`, apiError);
    return null;
  }
}; 

/**
 * 홈 띠배너 표시 상태 조회
 * @param lang 언어 설정 (기본값: 현재 설정된 언어)
 * @param addressId 주소 ID (필수)
 * @returns 띠배너 표시 상태
 */
export const getPromotionBannerStatus = async (
  lang?: string,
  addressId?: string
): Promise<PromotionBannerStatusResponse | null> => {
  if (!addressId) {
    return null;
  }

  const endpoint = '/api/v1/promotions/banner-status';
  const currentLang = lang || getCurrentLanguage();
  const apiLang = mapLanguageToApiParam(currentLang as SupportedLanguage);

  const params = new URLSearchParams();
  params.append('lang', apiLang);
  params.append('addressId', addressId);

  try {
    const response = await apiClient.get<PromotionBannerStatusResponse>(`${endpoint}?${params.toString()}`);
    return response.data;
  } catch (error) {
    const apiError = handleApiError(error);
    console.error(`Error fetching promotion banner status: ${apiError.message}`, apiError);
    return null;
  }
};